// buildLayoutEditorSlotControls.js

// Utilities
import {
	PAGE_NODE_PRESENTATION,
} from './LayoutNodePresentation';
import {
	getNodeBlockKey,
} from './pageEditorLayoutUtils';
import {
	buildPageViewToolbarActions,
} from './page/layoutPageViewConfig';
import {
	normalizeFieldBlockFieldKeys,
} from './layoutFieldDisplay';

function normalizeString(value) {
	return String(value || '').trim();
}

/**
 * buildLayoutEditorSlotControls
 *
 * Builds the toolbar and menu actions for a single layout editor slot.
 *
 * @param {boolean} allowFieldBlocks - Whether field blocks are enabled.
 * @param {boolean} allowVideoBlocks - Whether VIDEO blocks are enabled (content zones).
 * @param {number} assignedBlockCount - Count of currently assigned blocks.
 * @param {object} blocksByKey - Current block lookup map.
 * @param {array} fieldBlockOptions - Available field options for field blocks.
 * @param {function} handleCreateInlineRichTextBlock - Creates an inline rich-text block.
 * @param {boolean} allowContentZoneBlocks - Whether content zone blocks can be inserted.
 * @param {function} handleAssignContentZoneBlock - Creates a content zone block.
 * @param {function} handleMenuAction - Runs a menu action and closes the menu.
 * @param {function} handleOpenFieldBlockModal - Opens the field block modal.
 * @param {function} handleOpenNodeBlockModal - Opens the node block modal.
 * @param {function} handleOpenPageViewTypeModal - Opens the page view type picker.
 * @param {function} handleOpenRichTextBlockModal - Opens the rich-text block modal.
 * @param {function} handleOpenRollupBlockModal - Opens the rollup block modal.
 * @param {function} handleOpenViewBlockModal - Opens the view block modal.
 * @param {function} handleRemoveBlockFromSlot - Removes the current slot block.
 * @param {function} handleSetNodeBlockPresentation - Changes node block presentation.
 * @param {boolean} inlineCreateRichTextBlocks - Whether text blocks should be created inline.
 * @param {object} [options={}] - Slot movement and delete options.
 * @param {array|null} resolvedViewBlockAllowedTypeKeys - Allowed view type keys for the current editor surface.
 * @param {boolean} pageViewAuthoringEnabled - Whether page embed-only view authoring is enabled.
 * @param {boolean} dashboardViewAuthoringEnabled - Whether dashboard view authoring actions are enabled.
 * @param {function} handleOpenDashboardNewViewModal - Opens the dashboard new-view flow.
 * @param {function} handleOpenDashboardSavedViewModal - Opens the dashboard saved-view picker.
 * @param {object|null} slotNode - Slot node being controlled.
 * @returns {object} Slot control metadata and assembled actions.
 */
export function buildLayoutEditorSlotControls({
	allowContentZoneBlocks = true,
	allowFieldBlocks,
	allowNodeBlocks,
	allowVideoBlocks = false,
	assignedBlockCount,
	blocksByKey,
	fieldBlockOptions,
	handleAssignContentZoneBlock,
	handleAssignCoverBlock,
	handleCreateInlineRichTextBlock,
	handleAssignDiscussionBlock,
	handleMenuAction,
	handleOpenFieldBlockModal,
	handleOpenNodeBlockModal,
	handleOpenPageViewTypeModal,
	handleOpenRichTextBlockModal,
	handleOpenRollupBlockModal,
	handleOpenViewBlockModal,
	handleOpenDashboardNewViewModal,
	handleOpenDashboardSavedViewModal,
	handleRemoveBlockFromSlot,
	handleSetNodeBlockPresentation,
	handleTriggerVideoUpload,
	inlineCreateRichTextBlocks,
	options = {},
	pageViewAuthoringEnabled = false,
	dashboardViewAuthoringEnabled = false,
	resolvedViewBlockAllowedTypeKeys,
	slotNode,
}) {
	const onDelete = options?.onDelete;
	const handleSplitSlot = options?.handleSplitSlot;
	const slotKey = normalizeString(slotNode?.key);
	const block = blocksByKey[getNodeBlockKey(slotNode)] || null;
	const blockType = normalizeString(block?.type).toUpperCase();
	const isContentZoneBlock = blockType === 'CONTENT_ZONE';
	const isCoverBlock = blockType === 'COVER';
	const isDiscussionBlock = blockType === 'DISCUSSION';
	const isRichTextBlock = blockType === 'RICH_TEXT';
	const isRollupBlock = blockType === 'ROLLUP' && normalizeString(block?.rollupKey) && normalizeString(block?.typeKey);
	const isViewBlock = blockType === 'VIEW' && (
		Boolean(normalizeString(block?.viewId))
		|| Boolean(block?.view && typeof block.view === 'object')
	);
	const isFieldsBlock = (blockType === 'FIELDS' || blockType === 'FIELD') && normalizeFieldBlockFieldKeys(block?.fieldKeys).length > 0;
	const isNodeBlock = blockType === 'NODE' && normalizeString(block?.nodeKey);
	const isVideoBlock = blockType === 'VIDEO' && normalizeString(block?.nodeKey);
	const canEditFieldBlock = allowFieldBlocks === true && fieldBlockOptions.length > 0;
	const canEditNodeBlock = allowNodeBlocks === true;
	const canEditVideoBlock = allowVideoBlocks === true;
	const canRemoveAssignedBlock = assignedBlockCount > 1;
	const canDeleteSlot = typeof onDelete === 'function' && (!blockType || assignedBlockCount > 1);
	const canAddDashboardSavedView = dashboardViewAuthoringEnabled === true && !blockType;
	const canAddDashboardNewView = dashboardViewAuthoringEnabled === true && !blockType;
	const canAddPageView = !blockType && pageViewAuthoringEnabled === true;
	const canAddPageListView = !blockType
		&& !pageViewAuthoringEnabled
		&& Array.isArray(resolvedViewBlockAllowedTypeKeys)
		&& resolvedViewBlockAllowedTypeKeys.length > 0;

	const openViewAction = () => handleMenuAction(() => handleOpenViewBlockModal(slotKey));
	const openFieldAction = () => handleMenuAction(() => {
		handleOpenFieldBlockModal(slotKey);
		return Promise.resolve();
	});
	const openNodeAction = () => handleMenuAction(() => handleOpenNodeBlockModal(slotKey));
	const openRichTextAction = () => handleMenuAction(() => handleOpenRichTextBlockModal(slotKey));
	const addRichTextAction = () => handleMenuAction(() => {
		if (inlineCreateRichTextBlocks) {
			return handleCreateInlineRichTextBlock(slotKey);
		}

		handleOpenRichTextBlockModal(slotKey);
		return Promise.resolve();
	});
	const openRollupAction = () => handleMenuAction(() => handleOpenRollupBlockModal(slotKey));
	const openVideoAction = () => handleMenuAction(() => handleTriggerVideoUpload?.(slotKey));
	const removeBlockAction = () => handleMenuAction(() => handleRemoveBlockFromSlot(slotKey));

	const slotMenuActions = isContentZoneBlock ? [
		canRemoveAssignedBlock ? {
			label: 'Remove Zone',
			onClick: removeBlockAction,
		} : null,
		canDeleteSlot ? {
			label: 'Delete Block',
			onClick: () => handleMenuAction(onDelete),
		} : null,
	].filter(Boolean) : [
		isViewBlock ? {
			label: 'Change View',
			onClick: openViewAction,
		} : (isNodeBlock || isRollupBlock || isRichTextBlock ? {
			label: 'Replace With View',
			onClick: openViewAction,
		} : {
			label: 'Add View',
			onClick: openViewAction,
		}),
		canEditFieldBlock && isFieldsBlock ? {
			label: 'Change Fields',
			onClick: openFieldAction,
		} : (canEditFieldBlock && (isViewBlock || isNodeBlock || isRollupBlock || isRichTextBlock) ? {
			label: 'Replace With Fields',
			onClick: openFieldAction,
		} : (canEditFieldBlock ? {
			label: 'Add Fields',
			onClick: openFieldAction,
		} : null)),
		canEditNodeBlock && isNodeBlock ? {
			label: 'Change Node',
			onClick: openNodeAction,
		} : (canEditNodeBlock && (isViewBlock || isFieldsBlock || isRollupBlock || isRichTextBlock) ? {
			label: 'Replace With Node',
			onClick: openNodeAction,
		} : (canEditNodeBlock ? {
			label: 'Add Node',
			onClick: openNodeAction,
		} : null)),
		canEditVideoBlock && isVideoBlock ? {
			label: 'Replace Video',
			onClick: openVideoAction,
		} : (canEditVideoBlock && (isViewBlock || isFieldsBlock || isNodeBlock || isRollupBlock || isRichTextBlock || isDiscussionBlock || isCoverBlock) ? {
			label: 'Replace With Video',
			onClick: openVideoAction,
		} : (canEditVideoBlock ? {
			label: 'Add Video',
			onClick: openVideoAction,
		} : null)),
		isRichTextBlock ? {
			label: 'Edit Text',
			onClick: openRichTextAction,
		} : (isViewBlock || isFieldsBlock || isNodeBlock || isRollupBlock ? {
			label: 'Replace With Text',
			onClick: addRichTextAction,
		} : {
			label: 'Add Text',
			onClick: addRichTextAction,
		}),
		isRollupBlock ? {
			label: 'Change Rollup',
			onClick: openRollupAction,
		} : (isViewBlock || isFieldsBlock || isNodeBlock || isRichTextBlock || isDiscussionBlock || isCoverBlock ? {
			label: 'Replace With Rollup',
			onClick: openRollupAction,
		} : {
			label: 'Add Rollup',
			onClick: openRollupAction,
		}),
		isDiscussionBlock ? {
			label: 'Discussion Block',
			onClick: () => handleMenuAction(() => handleAssignDiscussionBlock(slotKey)),
		} : (isViewBlock || isFieldsBlock || isNodeBlock || isRichTextBlock || isRollupBlock || isCoverBlock ? {
			label: 'Replace With Discussion',
			onClick: () => handleMenuAction(() => handleAssignDiscussionBlock(slotKey)),
		} : {
			label: 'Add Discussion',
			onClick: () => handleMenuAction(() => handleAssignDiscussionBlock(slotKey)),
		}),
		isCoverBlock ? {
			label: 'Cover Photo Block',
			onClick: () => handleMenuAction(() => handleAssignCoverBlock(slotKey)),
		} : (isViewBlock || isFieldsBlock || isNodeBlock || isRichTextBlock || isRollupBlock || isDiscussionBlock ? {
			label: 'Replace With Cover Photo',
			onClick: () => handleMenuAction(() => handleAssignCoverBlock(slotKey)),
		} : {
			label: 'Add Cover Photo',
			onClick: () => handleMenuAction(() => handleAssignCoverBlock(slotKey)),
		}),
		isViewBlock && canRemoveAssignedBlock ? {
			label: 'Remove View',
			onClick: removeBlockAction,
		} : null,
		isFieldsBlock && canRemoveAssignedBlock ? {
			label: 'Remove Fields',
			onClick: removeBlockAction,
		} : null,
		canEditNodeBlock && isNodeBlock ? {
			label: 'Display as List',
			onClick: () => handleMenuAction(() => handleSetNodeBlockPresentation(slotKey, PAGE_NODE_PRESENTATION.LIST)),
		} : null,
		canEditNodeBlock && isNodeBlock ? {
			label: 'Display as Gallery',
			onClick: () => handleMenuAction(() => handleSetNodeBlockPresentation(slotKey, PAGE_NODE_PRESENTATION.GALLERY)),
		} : null,
		canEditNodeBlock && isNodeBlock ? {
			label: 'Display as Feed',
			onClick: () => handleMenuAction(() => handleSetNodeBlockPresentation(slotKey, PAGE_NODE_PRESENTATION.FEED)),
		} : null,
		isNodeBlock && canRemoveAssignedBlock ? {
			label: 'Remove Node',
			onClick: removeBlockAction,
		} : null,
		isVideoBlock && canRemoveAssignedBlock ? {
			label: 'Remove Video',
			onClick: removeBlockAction,
		} : null,
		isRichTextBlock && canRemoveAssignedBlock ? {
			label: 'Remove Text',
			onClick: removeBlockAction,
		} : null,
		isRollupBlock && canRemoveAssignedBlock ? {
			label: 'Remove Rollup',
			onClick: removeBlockAction,
		} : null,
		isDiscussionBlock && canRemoveAssignedBlock ? {
			label: 'Remove Discussion',
			onClick: removeBlockAction,
		} : null,
		isCoverBlock && canRemoveAssignedBlock ? {
			label: 'Remove Cover Photo',
			onClick: removeBlockAction,
		} : null,
		/*
		typeof onMoveUp === 'function' ? {
			label: 'Move Up',
			onClick: () => handleMenuAction(onMoveUp),
		} : null,
		typeof onMoveDown === 'function' ? {
			label: 'Move Down',
			onClick: () => handleMenuAction(onMoveDown),
		} : null,
		typeof onMoveLeft === 'function' ? {
			label: 'Move Left',
			onClick: () => handleMenuAction(onMoveLeft),
		} : null,
		typeof onMoveRight === 'function' ? {
			label: 'Move Right',
			onClick: () => handleMenuAction(onMoveRight),
		} : null,
		 */
		canDeleteSlot ? {
			label: 'Delete Block',
			onClick: () => handleMenuAction(onDelete),
		} : null,
	].filter(Boolean);

	const slotToolbarDashboardViewActions = canAddDashboardSavedView ? [
		{
			iconKey: 'view_kanban',
			label: 'Saved view',
			onClick: () => handleOpenDashboardSavedViewModal(slotKey),
			title: 'Add a saved view',
		},
		canAddDashboardNewView ? {
			iconKey: 'add',
			label: 'New view',
			onClick: () => handleOpenDashboardNewViewModal(slotKey),
			title: 'Create a new view',
		} : null,
	].filter(Boolean) : [];

	const slotToolbarAddActions = !blockType ? (
		dashboardViewAuthoringEnabled
			? slotToolbarDashboardViewActions
			: [
		pageViewAuthoringEnabled ? {
			iconKey: 'view_kanban',
			label: 'View',
			onClick: () => handleOpenPageViewTypeModal(slotKey, ''),
			title: 'Add a related view block',
		} : (!Array.isArray(resolvedViewBlockAllowedTypeKeys) ? {
			iconKey: 'view_kanban',
			label: 'View',
			onClick: () => handleOpenViewBlockModal(slotKey),
			title: 'Add a view block',
		} : null),
		canEditFieldBlock ? {
			iconKey: 'list_alt',
			label: 'Fields',
			onClick: () => handleOpenFieldBlockModal(slotKey),
			title: 'Add a fields block',
		} : null,
		/*
		canEditNodeBlock ? {
			iconKey: 'database',
			label: 'Data',
			onClick: () => handleOpenNodeBlockModal(slotKey),
			title: 'Add data block',
		} : null,
		 */
		{
			iconKey: 'text_fields',
			label: 'Text',
			onClick: () => {
				if (inlineCreateRichTextBlocks) {
					handleCreateInlineRichTextBlock(slotKey);
					return;
				}

				handleOpenRichTextBlockModal(slotKey);
			},
			title: 'Add a text block',
		},
		{
			iconKey: 'tag',
			label: 'Rollup',
			onClick: () => handleOpenRollupBlockModal(slotKey),
			title: 'Add a rollup block',
		},
		{
			iconKey: 'chat',
			label: 'Discussion',
			onClick: () => handleAssignDiscussionBlock(slotKey),
			title: 'Add a discussion block',
		},
		canEditVideoBlock ? {
			iconKey: 'videocam',
			label: 'Video',
			onClick: () => handleTriggerVideoUpload?.(slotKey),
			title: 'Upload a video block',
		} : null,
		{
			iconKey: 'image',
			label: 'Cover',
			onClick: () => handleAssignCoverBlock(slotKey),
			title: 'Add a cover photo block',
		},
		allowContentZoneBlocks ? {
			iconKey: 'dashboard_customize',
			label: 'Zone',
			onClick: () => handleAssignContentZoneBlock(slotKey),
			title: 'Add a content zone — unique per record',
		} : null,
	].filter(Boolean)
	) : [];

	const slotToolbarPageViewActions = canAddPageView || canAddPageListView
		? buildPageViewToolbarActions(handleOpenPageViewTypeModal, slotKey)
		: [];

	const slotToolbarStructureActions = [
		{
			iconKey: 'splitscreen_add',
			onClick: () => handleSplitSlot?.(slotKey, 'stack'),
			title: 'Add row',
		},
		{
			iconKey: 'splitscreen_vertical_add',
			onClick: () => handleSplitSlot?.(slotKey, 'row'),
			title: 'Add column',
		},
	];

	const primaryToolbarAction = isViewBlock ? {
		iconKey: 'edit',
		onClick: () => handleOpenViewBlockModal(slotKey),
		title: 'Change view',
	} : isFieldsBlock ? {
		iconKey: 'edit',
		onClick: () => handleOpenFieldBlockModal(slotKey),
		title: 'Change fields',
	} : (canEditNodeBlock && isNodeBlock) ? {
		iconKey: 'edit',
		onClick: () => handleOpenNodeBlockModal(slotKey),
		title: 'Change node',
	} : isRichTextBlock ? {
		iconKey: 'edit',
		onClick: () => handleOpenRichTextBlockModal(slotKey),
		title: 'Edit text',
	} : isRollupBlock ? {
		iconKey: 'edit',
		onClick: () => handleOpenRollupBlockModal(slotKey),
		title: 'Change rollup',
	} : isDiscussionBlock ? {
		iconKey: 'chat',
		onClick: () => handleAssignDiscussionBlock(slotKey),
		title: 'Discussion block',
	} : isVideoBlock ? {
		iconKey: 'videocam',
		onClick: () => handleTriggerVideoUpload?.(slotKey),
		title: 'Replace video',
	} : isCoverBlock ? {
		iconKey: 'image',
		onClick: () => {},
		title: 'Cover photo block',
	} : isContentZoneBlock ? {
		iconKey: 'info',
		onClick: () => {},
		title: 'Content is edited on each record Pages tab',
	} : null;

	const slotToolbarActions = [
		primaryToolbarAction,
		/*
		typeof onMoveLeft === 'function' ? {
			iconKey: 'chevron_left',
			onClick: () => handleMenuAction(onMoveLeft),
			title: 'Move block left',
		} : null,
		typeof onMoveRight === 'function' ? {
			iconKey: 'chevron_right',
			onClick: () => handleMenuAction(onMoveRight),
			title: 'Move block right',
		} : null,
		 */
		blockType && canRemoveAssignedBlock ? {
			iconKey: 'close',
			onClick: removeBlockAction,
			title: 'Remove block content',
		} : null,
		canDeleteSlot ? {
			iconKey: 'delete',
			onClick: () => handleMenuAction(onDelete),
			title: 'Delete block',
		} : null,
	].filter(Boolean);

	return {
		block,
		blockType,
		isContentZoneBlock,
		isCoverBlock,
		isDiscussionBlock,
		isFieldsBlock,
		isNodeBlock,
		isRichTextBlock,
		isRollupBlock,
		isVideoBlock,
		isViewBlock,
		primaryToolbarAction,
		slotMenuActions,
		slotToolbarActions,
		slotToolbarAddActions,
		slotToolbarPageViewActions,
		slotToolbarStructureActions,
	};
}